import { useCallback, useEffect, useMemo, useState } from 'react'
import { RankmiTable } from '@rankmi/rankmi-table'
import { Modal } from 'components/reusables/modal'
import { ModalButtons } from 'components/reusables/modalButtons'
import { PermissionsAlert } from './PermissionsAlert'
import { RankmiTableWrapper } from './styled'
import {
  IRestrictedResourceItem,
  IRestrictedResourcesModalProps,
  IRestrictedResourcesSummary,
} from './types'

const DEFAULT_PER_PAGE = 10
const DEFAULT_MAX_WIDTH = 800
const DEFAULT_MAX_HEIGHT = 640

const buildRestrictedRow = <T extends IRestrictedResourceItem>(
  label: string
): T =>
  ({
    id: -1,
    name: label,
    color: 'unset',
  } as T)

export const RestrictedResourcesModal = <
  T extends IRestrictedResourceItem = IRestrictedResourceItem
>({
  title,
  close,
  fetchData,
  columns,
  countIcon,
  locale,
  closeButtonText,
  restrictedRowLabel,
  alertConfig,
  renderAlert,
  maxWidth = DEFAULT_MAX_WIDTH,
  maxHeight = DEFAULT_MAX_HEIGHT,
  initialPerPage = DEFAULT_PER_PAGE,
  addButtonIcon,
  customEmpty,
  showFooter,
  renderRestrictedRow,
}: IRestrictedResourcesModalProps<T>) => {
  const [loading, setLoading] = useState(false)
  const [searchCriteria, setSearchCriteria] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [summary, setSummary] = useState<IRestrictedResourcesSummary<T>>()

  const restrictedCount = summary?.restrictedCount || 0
  const firstPage = currentPage === 1

  const loadData = useCallback(async () => {
    setLoading(true)
    try {
      const response = await fetchData({
        page: currentPage,
        search: searchCriteria,
        perPage: initialPerPage,
      })
      setSummary(response)
    } finally {
      setLoading(false)
    }
  }, [currentPage, fetchData, initialPerPage, searchCriteria])

  useEffect(() => {
    loadData()
  }, [loadData])

  const tableColumns = useMemo(
    () =>
      columns.map((column) => ({
        title: column.title,
        key: column.key,
        dataIndex: column.dataIndex,
        render: column.render
          ? (value: unknown, row: T) => column.render?.(value, row)
          : undefined,
      })),
    [columns]
  )

  const tableData = useMemo(() => {
    const allowedItems = summary?.allowedItems || []

    if (!firstPage || !restrictedCount) return allowedItems

    const restrictedRow =
      renderRestrictedRow?.(restrictedCount) ??
      buildRestrictedRow<T>(restrictedRowLabel(restrictedCount))

    return [restrictedRow, ...allowedItems]
  }, [
    firstPage,
    renderRestrictedRow,
    restrictedCount,
    restrictedRowLabel,
    summary?.allowedItems,
  ])

  let total = summary?.total || 0
  total -= restrictedCount

  const onSearch = (value: string) => {
    setSearchCriteria(value)
    setCurrentPage(1)
  }

  const onPaginationChange = (value: number) => {
    setCurrentPage(value)
  }

  return (
    <Modal
      isOpen
      title={title}
      maxwidth={maxWidth}
      maxheight={maxHeight}
      close={close}
      actions={
        <ModalButtons secondaryText={closeButtonText} secondaryAction={close} />
      }
    >
      <>
        {!!restrictedCount &&
          summary &&
          (renderAlert ? (
            renderAlert(summary)
          ) : alertConfig ? (
            <PermissionsAlert summary={summary} config={alertConfig} />
          ) : null)}
        <RankmiTableWrapper>
          <RankmiTable
            columnHeaders={tableColumns}
            addButtonIcon={addButtonIcon}
            data={tableData}
            search={searchCriteria}
            onSearch={onSearch}
            delaySearch
            countIcon={countIcon}
            onPaginationChange={onPaginationChange}
            currentPage={currentPage}
            total={total}
            locale={{
              header: {
                rowsCount: locale.rowsCount,
                rowsCount_plural: locale.rowsCount_plural,
                search: locale.search,
              },
            }}
            loading={loading}
            initialRowsPerPage={initialPerPage}
            customEmpty={customEmpty}
            showFooter={showFooter ?? total > 0}
          />
        </RankmiTableWrapper>
      </>
    </Modal>
  )
}

export default RestrictedResourcesModal
